/**
 * Unified analytics façade. Call site fires ONE event; this module routes
 * it to every installed provider (GA4 today). Keeps event-firing call
 * sites provider-agnostic so adding a destination later is one file
 * change here, not 30 across the components.
 *
 * Event names follow GA4 recommended event conventions
 * (https://support.google.com/analytics/answer/9267735) where possible:
 *
 *   `cta_click`              — primary CTA click
 *   `view_item`              — a reading/product card expanded to read details
 *   `select_item`            — service / product impression-to-interest
 *   `add_to_cart`            — service added to the cart
 *   `begin_checkout`         — booking modal opened / contribution flow entered
 *   `add_payment_info`       — payment step reached (PayPal buttons rendered)
 *   `checkout_attempt`       — buyer initiated payment (clicked Pay / Continue)
 *   `payment_failed`         — capture/PayPal error (an unsuccessful payment)
 *   `purchase`               — capture succeeded (a successful payment)
 *   `generate_lead`          — contact / testimonial form submitted
 *   `password_reset_request` — "Forgot password?" reset link requested
 *   `dashboard_nav`          — client-dashboard section navigation
 *
 * `product_type` segmentation: the ecommerce events carry a top-level
 * `product_type` param (`SESSION` | `CONTENT` | `MIXED` | `CONTRIBUTION`)
 * so reports can split the funnel by session-readings vs content products.
 * It's lifted to the event level (not just inside `items[]`) so it works
 * as an event-scoped custom dimension — see
 * `docs/runbooks/ga4-dashboard-config.md` §2.
 */
import { gtagEvent, gtagSetUserId, gtagSetUserProperties } from './gtag';

export type UserType = 'anon' | 'authenticated' | 'first_customer' | 'repeat_customer' | 'admin';

export function trackEvent(eventName: string, params?: Record<string, unknown>): void {
  gtagEvent(eventName, params);
}

/** Convenience: a click on a primary CTA, with a `cta_id` for grouping. */
export function trackCtaClick(ctaId: string, extra?: Record<string, unknown>): void {
  trackEvent('cta_click', { cta_id: ctaId, ...extra });
}

/**
 * Shared shape for the single-item events (`view_item`, `select_item`,
 * `add_to_cart`). Wraps one item in GA4's `items[]` array. If `extra`
 * carries a `product_type`, it's lifted to the TOP LEVEL of the event
 * (so it's selectable as an event-scoped custom dimension) AND mirrored
 * into the item as `item_category2` (so item-scoped reports can split on
 * it too). When `extra` has no `product_type`, the emitted shape is
 * byte-for-byte what it was before this helper existed.
 */
function trackSingleItemEvent(
  eventName: string,
  itemId: string | number,
  itemName: string,
  extra?: Record<string, unknown>
): void {
  const rest = extra ? { ...extra } : undefined;
  let productType: unknown;
  if (rest && 'product_type' in rest) {
    productType = rest.product_type;
    delete rest.product_type;
  }
  trackEvent(eventName, {
    ...(productType !== undefined ? { product_type: productType } : {}),
    items: [
      {
        item_id: String(itemId),
        item_name: itemName,
        ...(productType !== undefined ? { item_category2: productType } : {}),
        ...rest,
      },
    ],
  });
}

/** Convenience: a reading/product card opened to read its full details. */
export function trackViewItem(
  itemId: string | number,
  itemName: string,
  extra?: Record<string, unknown>
): void {
  trackSingleItemEvent('view_item', itemId, itemName, extra);
}

/** Convenience: a service / product impression-to-interest event. */
export function trackSelectItem(
  itemId: string | number,
  itemName: string,
  extra?: Record<string, unknown>
): void {
  trackSingleItemEvent('select_item', itemId, itemName, extra);
}

/** Convenience: a service / product added to the cart. */
export function trackAddToCart(
  itemId: string | number,
  itemName: string,
  extra?: Record<string, unknown>
): void {
  trackSingleItemEvent('add_to_cart', itemId, itemName, extra);
}

/**
 * Convenience: begin-checkout (BookingModal open, Contributions page entry).
 * `value` is in MAJOR units (dollars) — GA's recommended convention.
 */
export function trackBeginCheckout(
  value: number,
  currency: string,
  items: Array<{ item_id: string; item_name: string; price?: number }>,
  productType?: string
): void {
  trackEvent('begin_checkout', {
    value,
    currency,
    items,
    ...(productType ? { product_type: productType } : {}),
  });
}

/**
 * Convenience: payment step reached — the PayPal buttons (or any payment
 * method) are on screen. GA4 recommended `add_payment_info` event; the
 * middle of the checkout funnel between `begin_checkout` and `purchase`.
 */
export function trackAddPaymentInfo(
  value: number,
  currency: string,
  items: Array<{ item_id: string; item_name: string; price?: number }>,
  productType?: string
): void {
  trackEvent('add_payment_info', {
    value,
    currency,
    items,
    ...(productType ? { product_type: productType } : {}),
  });
}

/**
 * Convenience: the buyer actively initiated payment (clicked the PayPal
 * button / free-checkout Confirm). Custom event — distinct from
 * `begin_checkout` (modal merely opened) and from `purchase` (capture
 * succeeded). Pairs with `payment_failed` / `purchase` as the outcome.
 */
export function trackCheckoutAttempt(
  value: number,
  currency: string,
  items: Array<{ item_id: string; item_name: string; price?: number }>,
  productType?: string
): void {
  trackEvent('checkout_attempt', {
    value,
    currency,
    items,
    ...(productType ? { product_type: productType } : {}),
  });
}

/**
 * Convenience: an unsuccessful payment. Custom event. `reason` is a
 * short machine token (e.g. `paypal_error`, `capture_error`) — never a
 * raw provider error string, which can leak PII/secrets per CLAUDE.md.
 */
export function trackPaymentFailed(reason: string, extra?: Record<string, unknown>): void {
  trackEvent('payment_failed', { reason, ...extra });
}

/**
 * Convenience: completed purchase. Fires from the client immediately
 * after PayPal capture so attribution lands on the same session.
 */
export function trackPurchase(
  transactionId: string,
  value: number,
  currency: string,
  items: Array<{ item_id: string; item_name: string; price?: number; quantity?: number }>,
  productType?: string
): void {
  trackEvent('purchase', {
    transaction_id: transactionId,
    value,
    currency,
    items,
    ...(productType ? { product_type: productType } : {}),
  });
}

/** Form-completion event (contact, testimonial, etc.). */
export function trackGenerateLead(formId: string, extra?: Record<string, unknown>): void {
  trackEvent('generate_lead', { form_id: formId, ...extra });
}

/**
 * GA4 recommended event for successful sign-in. `method` describes the
 * mechanism — today every flow is `'email'`; if we add Google/Apple OAuth
 * later, pass `'google'` / `'apple'` and GA's standard reports segment
 * automatically.
 */
export function trackLogin(method: string): void {
  trackEvent('login', { method });
}

/**
 * GA4 recommended event for a successful new-account registration. Same
 * `method` convention as `trackLogin`.
 */
export function trackSignup(method: string): void {
  trackEvent('sign_up', { method });
}

/**
 * Fired when a user requests a password-reset link via the "Forgot
 * password?" flow. No email/PII is attached — the event records intent
 * only. GA4 has no recommended event for this; `password_reset_request`
 * is our own stable name.
 */
export function trackPasswordResetRequest(): void {
  trackEvent('password_reset_request', {});
}

/**
 * Client-dashboard section navigation. `section` is the in-app tab/area
 * the user switched to (e.g. `sessions`, `history`, `profile`,
 * `testimonials`). Lets us see which parts of the logged-in experience
 * actually get used post-purchase.
 */
export function trackDashboardNav(section: string): void {
  trackEvent('dashboard_nav', { dashboard_section: section });
}

/**
 * Set the visitor's `user_type` and `user_id` on GA. Safe to call
 * repeatedly — GA dedups identical sets.
 */
export function setVisitorClassification(
  userType: UserType,
  userId: number | null,
  purchaseCount: number
): void {
  const idString = userId == null ? null : String(userId);
  gtagSetUserId(idString);
  gtagSetUserProperties({ user_type: userType, purchase_count: purchaseCount });
}
